import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ukDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { updateLegalEvent } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

// Re-diarise a legal action (Emma #13): move the date, reason mandatory,
// logged on the event (rediarise_history) and the case timeline.
export default function RediariseDialog({ ev = {}, onClose, onDone }) {
  const [date, setDate] = useState(typeof ev.date === "string" ? ev.date.slice(0, 10) : "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const auth = useAuth() || {};
  const admin = typeof auth.isAdmin === "function" ? auth.isAdmin() : false;
  const user = auth.user;
  const ok = date && date !== (ev.date || "").slice(0, 10) && reason.trim().length >= 10;
  const submit = async () => {
    setBusy(true);
    const payload = { date, rediarise_reason: reason.trim(), actor: user?.name || "Ops", actor_is_admin: admin };
    const isTransient = (e) => !e?.response || e.response.status >= 500;   // Cloudflare 52x / origin blip
    try {
      try {
        await updateLegalEvent(ev.event_id, payload);
      } catch (e) {
        if (!isTransient(e)) throw e;
        await new Promise((r) => setTimeout(r, 1500));
        await updateLegalEvent(ev.event_id, payload);   // one quiet retry
      }
      toast.success(`Re-diarised to ${ukDate(date)}`);
      await onDone();
    } catch (e) {
      toast.error(errorDetailMessage(e, isTransient(e) ? "The server didn't respond (Cloudflare) — please try again in a moment; nothing was changed." : "Re-diarise failed"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="lcal-rediarise-dialog">
        <DialogHeader><DialogTitle>Re-diarise: {String(ev.label || ev.custom_label || ev.type || "action")}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Currently {ukDate(ev.date)}{ev.rediarise_count ? ` · already re-diarised ×${ev.rediarise_count}` : ""}. A reason is mandatory and is written to the case timeline.</p>
        <div className="space-y-2">
          <label className="text-xs font-medium">New date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm bg-white" data-testid="lcal-rediarise-date" />
          <label className="text-xs font-medium">Reason (what did you do before moving it?)</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. Chaser email sent to Central London County Court 21/09 — awaiting sealed Order"
                    className="w-full border rounded-md px-2 py-1.5 text-sm bg-white" data-testid="lcal-rediarise-reason" />
          {reason.length > 0 && reason.trim().length < 10 && <p className="text-[11px] text-red-600">Reason must be at least 10 characters.</p>}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={!ok || busy} data-testid="lcal-rediarise-confirm">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Move & log reason"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

